import {
	type Action,
	type BoundActions,
	createContainer,
	createHook,
	createStore,
	type HookFunction,
	type OverrideContainerComponent,
} from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags';
import { type AllTeamsQuery, type OriginQuery, teamsClient } from '@atlaskit/teams-client';
import {
	type TeamAgentAssociation,
	type TeamMembership,
	type TeamMembershipSettings,
	type TeamWithImageUrls,
	type User,
} from '@atlaskit/teams-client/types';
import type { TeamsPermissions } from '@atlassian/teams-common/use-teams-permissions';

import {
	acceptTeamInvitation,
	approveJoinRequest,
	declineJoinRequest,
	getMyTeamMembership,
	getRequestingToJoinUsers,
	getTeamAgents,
	getTeamMembers,
	getTeamProfile,
	inviteAgentsToTeam,
	inviteUsersToTeam,
	joinTeam,
	prefillTeamData,
	removeTeamAgent,
	removeTeamAgentAssociation,
	removeTeamMember,
} from './actions';
import { addAgentsToTeam } from './actions/add-agent';
import { archiveTeam, unarchiveTeam } from './actions/change-team-state';
import getAllTeams from './actions/get-all-teams';
import { unlinkExternalTeam } from './actions/unlink-external-team';
import type {
	MyMembershipState,
	StoreApi,
	TeamAgentsState,
	TeamAppState,
	TeamMembersState,
	TeamProfileState,
} from './types';

const initialState: TeamAppState = {
	teamProfile: {
		teamId: '',
		isLoading: true,
		hasLoaded: false,
		errorType: null,
		data: {},
	},
	teamMembers: {
		teamId: '',
		isLoading: true,
		isLoadingNextPage: false,
		hasLoaded: false,
		hasError: false,
		errorType: null,
		members: [],
		requestingToJoinUsers: [],
		membersPageInfo: {
			hasNextPage: false,
			cursor: null,
		},
		requestingToJoinUsersPageInfo: {
			hasNextPage: false,
			cursor: null,
		},
	},
	teamAgents: {
		teamId: '',
		isLoading: true,
		isLoadingNextPage: false,
		hasLoaded: false,
		hasError: false,
		errorType: null,
		members: [],
		agents: [],
	},
	myTeamMembership: {
		teamId: '',
		hasLoaded: false,
		membership: undefined,
	},
	exitedTeams: new Set(),
	allTeamsData: {
		teams: [],
		isLoading: true,
		hasError: false,
		cursor: '',
	},
};

export type TeamActions = typeof actions;

const actions: {
	/**
	 * @deprecated set the config on the teamsClient using useTeamsClientSetup
	 */
	setTeamClientRootUrl: (rootUrl: string) => Action<TeamAppState>;
	collectTeamData: (
		teamId: string,
		forceRefresh?: boolean,
	) => ({ dispatch, getState }: StoreApi) => Promise<void>;
	restoreTeam: (
		teamId: string,
		options?: { fetchTeamData?: boolean },
	) => ({ dispatch }: StoreApi) => Promise<void>;
	fetchTeamMembersNextPage: (
		teamId: string,
		count?: number,
	) => ({ dispatch, getState }: StoreApi) => Promise<void>;
	refreshRequestingToJoinUsers: (
		teamId: string,
	) => ({ dispatch, getState }: StoreApi) => Promise<TeamMembership[]>;
	prefillTeamData: (
		teamId: string,
		teamData: TeamWithImageUrls,
		members: User[],
		currentUser: User,
	) => ({ getState, setState, dispatch }: StoreApi) => void;
	updateTeamProfile: (
		teamId: string,
		data: {
			displayName?: string;
			description?: string;
			headerImageId?: string;
			membershipSettings?: TeamMembershipSettings;
		},
	) => ({ dispatch, getState, setState }: StoreApi) => Promise<TeamWithImageUrls>;
	acceptAllJoinRequests: (teamId: string) => ({ dispatch, getState }: StoreApi) => Promise<void>;
	declineAllJoinRequests: (teamId: string) => ({ dispatch, getState }: StoreApi) => Promise<void>;
	// Throws if we cannot remove the member
	removeTeamMember: (
		teamId: string,
		memberId: string,
		permissions?: Partial<TeamsPermissions>,
	) => ({ getState, setState }: StoreApi) => Promise<void>;
	removeTeamAgent: (
		teamId: string,
		agentAtlassianAccountId: string,
		permissions?: Partial<TeamsPermissions>,
	) => ({ getState, setState }: StoreApi) => Promise<void>;
	removeTeamAgentAssociation: (
		teamId: string,
		agentId: string,
		onRemoveAgentSuccess?: (agentId: string, teamId: string) => void,
	) => ({ getState, setState }: StoreApi) => Promise<void>;
	revokeTeamInvite: (
		teamId: string,
		memberId: string,
	) => ({ getState, setState }: StoreApi) => Promise<void>;
	// Throws if teamsClient.selfJoinTeam throws
	joinTeam: (
		teamId: string,
		originQuery: OriginQuery,
		user: User,
	) => ({ dispatch }: StoreApi) => Promise<TeamMembership>;
	// Throws if teamsClient.cancelJoinRequest throws
	cancelJoinRequest: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
	// Throws if teamsClient.acceptTeamInvitation throws
	acceptTeamInvitation: (
		teamId: string,
		currentUserId: string,
	) => ({ dispatch, getState }: StoreApi) => Promise<void>;
	// Throws if teamsClient.declineTeamInvitation throws
	declineTeamInvitation: (teamId: string) => ({ getState }: StoreApi) => Promise<void>;
	// Throws if teamsClient.inviteUsersToTeam or publicApiClient.addUsersToTeam throws
	inviteUsersToTeam: (
		teamId: string,
		members: {
			id: string;
			email?: string;
			avatarUrl?: string;
			name: string;
			title?: string;
		}[],
		originQuery: OriginQuery,
	) => ({
		dispatch,
		getState,
		setState,
	}: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
	inviteAgentsToTeam: (
		teamId: string,
		members: {
			id: string;
			email?: string;
			avatarUrl?: string;
			name: string;
		}[],
	) => ({ getState, setState }: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
	addAgentsToTeam: (
		teamId: string,
		agents: {
			id: string;
			avatarUrl?: string;
			name: string;
		}[],
	) => ({ getState, setState }: StoreApi) => Promise<Required<TeamAgentAssociation>[]>;
	// Throws if teamsClient.approveJoinRequest throws
	approveJoinRequest: (
		teamId: string,
		memberId: string,
		user: User,
	) => ({ getState, setState }: StoreApi) => Promise<void>;
	// Throws if teamsClient.declineJoinRequest throws
	declineJoinRequest: (
		teamId: string,
		memberId: string,
	) => ({ getState, setState }: StoreApi) => Promise<void>;
	// Throws if teamsClient.deleteTeam throws
	deleteTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
	archiveTeam: (
		teamId: string,
	) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
	unarchiveTeam: (
		teamId: string,
	) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
	// disconnecting the team from the group before archiving the team
	unlinkExternalTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
	getAllTeams: (
		allTeamsQuery: AllTeamsQuery,
		keepExistingData?: boolean,
	) => ({ dispatch }: StoreApi) => Promise<void>;
	updateTeamMembershipSettings: (
		teamId: string,
		membershipSettings: TeamMembershipSettings,
	) => ({ getState, setState }: StoreApi) => Promise<void>;
} = {
	/**
	 * @deprecated set the config on the teamsClient using useTeamsClientSetup
	 */
	setTeamClientRootUrl:
		(rootUrl: string): Action<TeamAppState> =>
		() => {
			teamsClient.setLegionRootUrl(rootUrl);
		},
	collectTeamData:
		(teamId: string, forceRefresh?: boolean) =>
		async ({ dispatch, getState }: StoreApi): Promise<void> => {
			// If we already have the team's data, do not collect it again
			if (!forceRefresh && getState().teamProfile.teamId === teamId) {
				return;
			}

			await Promise.all([
				dispatch(getTeamProfile(teamId)),
				dispatch(getMyTeamMembership(teamId)),
				dispatch(getTeamMembers(teamId)),
				fg('add_agents_in_new_connection_pattern') && dispatch(getTeamAgents(teamId)),
			]);

			void dispatch(getRequestingToJoinUsers(teamId));
		},
	restoreTeam:
		(teamId: string, options?: { fetchTeamData?: boolean }) =>
		async ({ dispatch }: StoreApi): Promise<void> => {
			await teamsClient.restoreTeam(teamId);

			if (options?.fetchTeamData) {
				await Promise.all([
					dispatch(getTeamProfile(teamId)),
					dispatch(getMyTeamMembership(teamId)),
					dispatch(getTeamMembers(teamId)),
					fg('add_agents_in_new_connection_pattern') && dispatch(getTeamAgents(teamId)),
				]);
			}
		},
	fetchTeamMembersNextPage:
		(teamId: string, count: number = 100) =>
		async ({ dispatch, getState }: StoreApi): Promise<void> => {
			if (!getState().teamMembers.membersPageInfo.hasNextPage) {
				return;
			}

			await dispatch(getTeamMembers(teamId, true, count));
		},
	refreshRequestingToJoinUsers:
		(teamId: string) =>
		async ({ dispatch, getState }: StoreApi): Promise<TeamMembership[]> => {
			// Check that we're refreshing data for the current team
			if (getState().teamProfile.teamId !== teamId) {
				return [];
			}

			return await dispatch(getRequestingToJoinUsers(teamId));
		},

	prefillTeamData,

	updateTeamProfile:
		(
			teamId: string,
			data: {
				displayName?: string;
				description?: string;
				headerImageId?: string;
				membershipSettings?: TeamMembershipSettings;
			},
		) =>
		async ({ dispatch, getState, setState }: StoreApi): Promise<TeamWithImageUrls> => {
			const isTransitioningToOpen =
				data.membershipSettings === 'OPEN' &&
				getState().teamProfile.data.membershipSettings === 'MEMBER_INVITE';

			const response = await teamsClient.updateTeamById(teamId, data);

			setState({
				teamProfile: {
					...getState().teamProfile,
					data: response,
				},
			});

			if (isTransitioningToOpen) {
				dispatch(getTeamMembers(teamId));
			}

			return response;
		},

	acceptAllJoinRequests:
		(teamId: string) =>
		async ({ dispatch, getState }: StoreApi): Promise<void> => {
			const requests = getState().teamMembers.requestingToJoinUsers;

			await Promise.all(
				requests.map((request) => {
					return dispatch(approveJoinRequest(teamId, request.membershipId.memberId, request.user!));
				}),
			);
		},

	declineAllJoinRequests:
		(teamId: string) =>
		async ({ dispatch, getState }: StoreApi): Promise<void> => {
			const requests = getState().teamMembers.requestingToJoinUsers;

			await Promise.all(
				requests.map((request) => {
					return dispatch(declineJoinRequest(teamId, request.membershipId.memberId));
				}),
			);
		},

	// Throws if we cannot remove the member
	removeTeamMember,

	removeTeamAgent,

	removeTeamAgentAssociation,

	revokeTeamInvite:
		(teamId: string, memberId: string) =>
		async ({ getState, setState }: StoreApi): Promise<void> => {
			const {
				teamMembers: { teamId: currentTeamId },
			} = getState();
			const removedInvite = await teamsClient.revokeTeamInvite(teamId, memberId);

			if (removedInvite && removedInvite.state !== 'ALUMNI') {
				throw Error('Invite claims to be cancelled but was not');
			}

			if (currentTeamId !== teamId) {
				return;
			}

			setState({
				teamMembers: {
					...getState().teamMembers,
					members: getState().teamMembers.members.filter(
						(member) => !!member.user && member.user.id !== memberId,
					),
				},
			});
		},

	// Throws if teamsClient.selfJoinTeam throws
	joinTeam,

	// Throws if teamsClient.cancelJoinRequest throws
	cancelJoinRequest:
		(teamId: string) =>
		async ({ getState, setState }: StoreApi): Promise<void> => {
			const {
				teamProfile: { teamId: currentTeamId },
			} = getState();
			await teamsClient.cancelJoinRequest(teamId);

			const state = getState();

			if (currentTeamId !== teamId) {
				return;
			}

			setState({
				...state,
				myTeamMembership: {
					teamId,
					hasLoaded: true,
					membership: undefined,
				},
			});
		},

	// Throws if teamsClient.acceptTeamInvitation throws
	acceptTeamInvitation,

	// Throws if teamsClient.declineTeamInvitation throws
	declineTeamInvitation:
		(teamId: string) =>
		async ({ getState }: StoreApi): Promise<void> => {
			const { exitedTeams } = getState();
			await teamsClient.declineTeamInvitation(teamId);

			exitedTeams.add(teamId);

			// No need to re-fetch, when an invite is declined we navigate away
		},

	// Throws if teamsClient.inviteUsersToTeam or publicApiClient.addUsersToTeam throws
	inviteUsersToTeam,

	inviteAgentsToTeam,

	addAgentsToTeam,

	// Throws if teamsClient.approveJoinRequest throws
	approveJoinRequest,

	// Throws if teamsClient.declineJoinRequest throws
	declineJoinRequest,

	// Throws if teamsClient.deleteTeam throws
	deleteTeam:
		(teamId: string) =>
		async ({ getState, setState }: StoreApi): Promise<void> => {
			const { exitedTeams } = getState();
			await teamsClient.deleteTeam(teamId);
			setState({
				teamProfile: initialState.teamProfile,
				teamMembers: initialState.teamMembers,
				myTeamMembership: initialState.myTeamMembership,
			});

			exitedTeams.add(teamId);
		},

	archiveTeam,

	unarchiveTeam,

	// disconnecting the team from the group before archiving the team
	unlinkExternalTeam,

	getAllTeams:
		(allTeamsQuery: AllTeamsQuery, keepExistingData?: boolean) =>
		async ({ dispatch }: StoreApi): Promise<void> => {
			await dispatch(getAllTeams(allTeamsQuery, keepExistingData));
		},

	updateTeamMembershipSettings:
		(teamId: string, membershipSettings: TeamMembershipSettings) =>
		async ({ getState, setState }: StoreApi): Promise<void> => {
			const state = getState();

			if (state.teamProfile.teamId !== teamId) {
				return;
			}

			setState({
				...state,
				teamProfile: {
					...state.teamProfile,
					data: {
						...(state.teamProfile.data as TeamWithImageUrls),
						membershipSettings,
					},
				},
			});
		},
};

const TeamStore = createStore<TeamAppState, TeamActions>({
	initialState,
	actions,
});

/**
 * Wraps subtrees that use team sweet-state hooks (useTeamProfileData, members, etc.) so they use
 * an isolated store instance. Pass a `scope` string for surfaces that must not share state with
 * the main app (e.g. team preview panel vs full page).
 */
export const TeamStoreContainer: OverrideContainerComponent<unknown> = createContainer(TeamStore);

/* Profile */
export const useTeamProfileDataState: HookFunction<
	TeamProfileState,
	BoundActions<
		TeamAppState,
		{
			/**
			 * @deprecated set the config on the teamsClient using useTeamsClientSetup
			 */
			setTeamClientRootUrl: (rootUrl: string) => Action<TeamAppState>;
			collectTeamData: (
				teamId: string,
				forceRefresh?: boolean,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			restoreTeam: (
				teamId: string,
				options?: { fetchTeamData?: boolean },
			) => ({ dispatch }: StoreApi) => Promise<void>;
			fetchTeamMembersNextPage: (
				teamId: string,
				count?: number,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			refreshRequestingToJoinUsers: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<TeamMembership[]>;
			prefillTeamData: (
				teamId: string,
				teamData: TeamWithImageUrls,
				members: User[],
				currentUser: User,
			) => ({ getState, setState, dispatch }: StoreApi) => void;
			updateTeamProfile: (
				teamId: string,
				data: {
					displayName?: string;
					description?: string;
					headerImageId?: string;
					membershipSettings?: TeamMembershipSettings;
				},
			) => ({ dispatch, getState, setState }: StoreApi) => Promise<TeamWithImageUrls>;
			acceptAllJoinRequests: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			declineAllJoinRequests: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			// Throws if we cannot remove the member
			removeTeamMember: (
				teamId: string,
				memberId: string,
				permissions?: Partial<TeamsPermissions>,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			removeTeamAgent: (
				teamId: string,
				agentAtlassianAccountId: string,
				permissions?: Partial<TeamsPermissions>,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			removeTeamAgentAssociation: (
				teamId: string,
				agentId: string,
				onRemoveAgentSuccess?: (agentId: string, teamId: string) => void,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			revokeTeamInvite: (
				teamId: string,
				memberId: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.selfJoinTeam throws
			joinTeam: (
				teamId: string,
				originQuery: OriginQuery,
				user: User,
			) => ({ dispatch }: StoreApi) => Promise<TeamMembership>;
			// Throws if teamsClient.cancelJoinRequest throws
			cancelJoinRequest: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.acceptTeamInvitation throws
			acceptTeamInvitation: (
				teamId: string,
				currentUserId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.declineTeamInvitation throws
			declineTeamInvitation: (teamId: string) => ({ getState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.inviteUsersToTeam or publicApiClient.addUsersToTeam throws
			inviteUsersToTeam: (
				teamId: string,
				members: {
					id: string;
					email?: string;
					avatarUrl?: string;
					name: string;
					title?: string;
				}[],
				originQuery: OriginQuery,
			) => ({
				dispatch,
				getState,
				setState,
			}: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
			inviteAgentsToTeam: (
				teamId: string,
				members: {
					id: string;
					email?: string;
					avatarUrl?: string;
					name: string;
				}[],
			) => ({ getState, setState }: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
			addAgentsToTeam: (
				teamId: string,
				agents: {
					id: string;
					avatarUrl?: string;
					name: string;
				}[],
			) => ({ getState, setState }: StoreApi) => Promise<Required<TeamAgentAssociation>[]>;
			// Throws if teamsClient.approveJoinRequest throws
			approveJoinRequest: (
				teamId: string,
				memberId: string,
				user: User,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.declineJoinRequest throws
			declineJoinRequest: (
				teamId: string,
				memberId: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.deleteTeam throws
			deleteTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			archiveTeam: (
				teamId: string,
			) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
			unarchiveTeam: (
				teamId: string,
			) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
			// disconnecting the team from the group before archiving the team
			unlinkExternalTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			getAllTeams: (
				allTeamsQuery: AllTeamsQuery,
				keepExistingData?: boolean,
			) => ({ dispatch }: StoreApi) => Promise<void>;
			updateTeamMembershipSettings: (
				teamId: string,
				membershipSettings: TeamMembershipSettings,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
		}
	>,
	void
> = createHook(TeamStore, {
	selector: (state) => state.teamProfile,
});

/* Members */
export const useTeamMembersState: HookFunction<
	TeamMembersState,
	BoundActions<TeamAppState, TeamActions>
> = createHook(TeamStore, {
	selector: (state) => state.teamMembers,
});

/* My membership */
export const useMyMembership: HookFunction<
	MyMembershipState,
	BoundActions<
		TeamAppState,
		{
			/**
			 * @deprecated set the config on the teamsClient using useTeamsClientSetup
			 */
			setTeamClientRootUrl: (rootUrl: string) => Action<TeamAppState>;
			collectTeamData: (
				teamId: string,
				forceRefresh?: boolean,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			restoreTeam: (
				teamId: string,
				options?: { fetchTeamData?: boolean },
			) => ({ dispatch }: StoreApi) => Promise<void>;
			fetchTeamMembersNextPage: (
				teamId: string,
				count?: number,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			refreshRequestingToJoinUsers: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<TeamMembership[]>;
			prefillTeamData: (
				teamId: string,
				teamData: TeamWithImageUrls,
				members: User[],
				currentUser: User,
			) => ({ getState, setState, dispatch }: StoreApi) => void;
			updateTeamProfile: (
				teamId: string,
				data: {
					displayName?: string;
					description?: string;
					headerImageId?: string;
					membershipSettings?: TeamMembershipSettings;
				},
			) => ({ dispatch, getState, setState }: StoreApi) => Promise<TeamWithImageUrls>;
			acceptAllJoinRequests: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			declineAllJoinRequests: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			// Throws if we cannot remove the member
			removeTeamMember: (
				teamId: string,
				memberId: string,
				permissions?: Partial<TeamsPermissions>,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			removeTeamAgent: (
				teamId: string,
				agentAtlassianAccountId: string,
				permissions?: Partial<TeamsPermissions>,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			removeTeamAgentAssociation: (
				teamId: string,
				agentId: string,
				onRemoveAgentSuccess?: (agentId: string, teamId: string) => void,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			revokeTeamInvite: (
				teamId: string,
				memberId: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.selfJoinTeam throws
			joinTeam: (
				teamId: string,
				originQuery: OriginQuery,
				user: User,
			) => ({ dispatch }: StoreApi) => Promise<TeamMembership>;
			// Throws if teamsClient.cancelJoinRequest throws
			cancelJoinRequest: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.acceptTeamInvitation throws
			acceptTeamInvitation: (
				teamId: string,
				currentUserId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.declineTeamInvitation throws
			declineTeamInvitation: (teamId: string) => ({ getState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.inviteUsersToTeam or publicApiClient.addUsersToTeam throws
			inviteUsersToTeam: (
				teamId: string,
				members: {
					id: string;
					email?: string;
					avatarUrl?: string;
					name: string;
					title?: string;
				}[],
				originQuery: OriginQuery,
			) => ({
				dispatch,
				getState,
				setState,
			}: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
			inviteAgentsToTeam: (
				teamId: string,
				members: {
					id: string;
					email?: string;
					avatarUrl?: string;
					name: string;
				}[],
			) => ({ getState, setState }: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
			addAgentsToTeam: (
				teamId: string,
				agents: {
					id: string;
					avatarUrl?: string;
					name: string;
				}[],
			) => ({ getState, setState }: StoreApi) => Promise<Required<TeamAgentAssociation>[]>;
			// Throws if teamsClient.approveJoinRequest throws
			approveJoinRequest: (
				teamId: string,
				memberId: string,
				user: User,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.declineJoinRequest throws
			declineJoinRequest: (
				teamId: string,
				memberId: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.deleteTeam throws
			deleteTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			archiveTeam: (
				teamId: string,
			) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
			unarchiveTeam: (
				teamId: string,
			) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
			// disconnecting the team from the group before archiving the team
			unlinkExternalTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			getAllTeams: (
				allTeamsQuery: AllTeamsQuery,
				keepExistingData?: boolean,
			) => ({ dispatch }: StoreApi) => Promise<void>;
			updateTeamMembershipSettings: (
				teamId: string,
				membershipSettings: TeamMembershipSettings,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
		}
	>,
	void
> = createHook(TeamStore, {
	selector: (state) => state.myTeamMembership,
});

/* Exited teams list */
export const useExitedTeams: HookFunction<
	Set<string>,
	BoundActions<
		TeamAppState,
		{
			/**
			 * @deprecated set the config on the teamsClient using useTeamsClientSetup
			 */
			setTeamClientRootUrl: (rootUrl: string) => Action<TeamAppState>;
			collectTeamData: (
				teamId: string,
				forceRefresh?: boolean,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			restoreTeam: (
				teamId: string,
				options?: { fetchTeamData?: boolean },
			) => ({ dispatch }: StoreApi) => Promise<void>;
			fetchTeamMembersNextPage: (
				teamId: string,
				count?: number,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			refreshRequestingToJoinUsers: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<TeamMembership[]>;
			prefillTeamData: (
				teamId: string,
				teamData: TeamWithImageUrls,
				members: User[],
				currentUser: User,
			) => ({ getState, setState, dispatch }: StoreApi) => void;
			updateTeamProfile: (
				teamId: string,
				data: {
					displayName?: string;
					description?: string;
					headerImageId?: string;
					membershipSettings?: TeamMembershipSettings;
				},
			) => ({ dispatch, getState, setState }: StoreApi) => Promise<TeamWithImageUrls>;
			acceptAllJoinRequests: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			declineAllJoinRequests: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			// Throws if we cannot remove the member
			removeTeamMember: (
				teamId: string,
				memberId: string,
				permissions?: Partial<TeamsPermissions>,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			removeTeamAgent: (
				teamId: string,
				agentAtlassianAccountId: string,
				permissions?: Partial<TeamsPermissions>,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			removeTeamAgentAssociation: (
				teamId: string,
				agentId: string,
				onRemoveAgentSuccess?: (agentId: string, teamId: string) => void,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			revokeTeamInvite: (
				teamId: string,
				memberId: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.selfJoinTeam throws
			joinTeam: (
				teamId: string,
				originQuery: OriginQuery,
				user: User,
			) => ({ dispatch }: StoreApi) => Promise<TeamMembership>;
			// Throws if teamsClient.cancelJoinRequest throws
			cancelJoinRequest: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.acceptTeamInvitation throws
			acceptTeamInvitation: (
				teamId: string,
				currentUserId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.declineTeamInvitation throws
			declineTeamInvitation: (teamId: string) => ({ getState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.inviteUsersToTeam or publicApiClient.addUsersToTeam throws
			inviteUsersToTeam: (
				teamId: string,
				members: {
					id: string;
					email?: string;
					avatarUrl?: string;
					name: string;
					title?: string;
				}[],
				originQuery: OriginQuery,
			) => ({
				dispatch,
				getState,
				setState,
			}: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
			inviteAgentsToTeam: (
				teamId: string,
				members: {
					id: string;
					email?: string;
					avatarUrl?: string;
					name: string;
				}[],
			) => ({ getState, setState }: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
			addAgentsToTeam: (
				teamId: string,
				agents: {
					id: string;
					avatarUrl?: string;
					name: string;
				}[],
			) => ({ getState, setState }: StoreApi) => Promise<Required<TeamAgentAssociation>[]>;
			// Throws if teamsClient.approveJoinRequest throws
			approveJoinRequest: (
				teamId: string,
				memberId: string,
				user: User,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.declineJoinRequest throws
			declineJoinRequest: (
				teamId: string,
				memberId: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			// Throws if teamsClient.deleteTeam throws
			deleteTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			archiveTeam: (
				teamId: string,
			) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
			unarchiveTeam: (
				teamId: string,
			) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
			// disconnecting the team from the group before archiving the team
			unlinkExternalTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			getAllTeams: (
				allTeamsQuery: AllTeamsQuery,
				keepExistingData?: boolean,
			) => ({ dispatch }: StoreApi) => Promise<void>;
			updateTeamMembershipSettings: (
				teamId: string,
				membershipSettings: TeamMembershipSettings,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
		}
	>,
	void
> = createHook(TeamStore, {
	selector: (state) => state.exitedTeams,
});

export const useTeamAgentsState: HookFunction<
	TeamAgentsState,
	BoundActions<TeamAppState, TeamActions>
> = createHook(TeamStore, {
	selector: (state) => state.teamAgents,
});
